/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import IVdiModel, { IVdiDialogParams, IVdiStrings, VdiDialogType } from './VdiModel';
import { REMOTE_DESKTOP_TYPE } from 'apmui/resource/remoteDesktop/Model';
import { INPUT } from 'apmui/components/form/Model';
import PageType from 'apmui/page/PageType';
import ResourceType from 'apmui/resource/ResourceType';
import Object from 'apmui/util/Object';
import deepCopy from 'apmui/util/deepCopy';

import IDialogConfiguration, {
    DialogClass,
    DialogType,
    CommonDialogAction,
    IDialogEntry,
} from 'apmui/components/dialog/Model';
import * as Dialog from 'apmui/components/dialog/DialogHelper';
import VdiError, { VdiErrorType } from 'apmui/resource/remoteDesktop/VdiError';
import { VDI_CLIENT, getUserPreferredClient, setUserPreferredClient } from 'apmui/resource/remoteDesktop/VdiClientInfo';
import getCitrixClients from 'apmui/resource/remoteDesktop/CitrixClient';
import getViewClients from 'apmui/resource/remoteDesktop/VmwareViewClient';

const CITRIX_DEFAULT_URL = 'http://receiver.citrix.com';

const app = App.get();

function vdiGetDialogTemplates(strings: Readonly<IVdiStrings>): Record<VdiDialogType, IDialogConfiguration> {
    const defaultEntries: IDialogEntry[] = [
        {
            id: CommonDialogAction.CANCEL,
            caption: strings.cancel_button,
        },
        {
            id: CommonDialogAction.OK,
            style: 'submit',
            caption: strings.ok_button,
        },
    ];

    return {
        [DialogType.VDI_DESTINATION]: {
            id: DialogType.VDI_DESTINATION,
            class: DialogClass.VDI,
            title: strings.user_def_dst_form_caption,
            showCloseButton: true,
            closeTitle: strings.close_button,
            entries: [
                {
                    name: 'destination',
                    type: INPUT.TEXT,
                    caption: strings.user_def_dst_label,
                },
                ...defaultEntries,
            ],
        },
        [DialogType.VDI_ERROR]: {
            id: DialogType.VDI_ERROR,
            class: DialogClass.VDI,
            title: '',
            showCloseButton: true,
            closeTitle: strings.close_button,
            entries: [
                {
                    id: CommonDialogAction.CANCEL,
                    caption: strings.close_button,
                },
                {
                    id: CommonDialogAction.OK,
                    style: 'submit',
                    caption: strings.retry_button,
                },
            ],
        },
        [DialogType.VDI_LAUNCH_CLIENT]: {
            id: DialogType.VDI_LAUNCH_CLIENT,
            class: DialogClass.VDI,
            title: strings.select_client_caption,
            showCloseButton: true,
            closeTitle: strings.close_button,
            entries: [...defaultEntries],
        },
        [DialogType.VDI_LOGON]: {
            id: DialogType.VDI_LOGON,
            class: DialogClass.VDI,
            title: strings.logon_form_caption,
            showCloseButton: true,
            closeTitle: strings.close_button,
            entries: [
                {
                    name: 'username',
                    type: INPUT.TEXT,
                    caption: strings.username_label,
                    disabled: false,
                },
                {
                    name: 'password',
                    type: INPUT.PASSWORD,
                    caption: strings.password_label,
                    disabled: false,
                },
                {
                    name: 'domain',
                    type: INPUT.TEXT,
                    caption: strings.domain_label,
                    disabled: false,
                },
                {
                    id: CommonDialogAction.CANCEL,
                    caption: strings.cancel_button,
                },
                {
                    id: CommonDialogAction.OK,
                    style: 'submit',
                    caption: strings.logon_button,
                },
            ],
        },
        [DialogType.VDI_SETTINGS]: {
            id: DialogType.VDI_SETTINGS,
            class: DialogClass.VDI,
            title: strings.settings_dialog_caption,
            showCloseButton: true,
            closeTitle: strings.close_button,
            entries: [...defaultEntries],
        },
    };
}

function vdiInitLaunchDialog(dialog: IDialogConfiguration, vdi: Readonly<IVdiModel>, type: REMOTE_DESKTOP_TYPE) {
    const clientInfo = vdi.clients && vdi.clients[type];
    if (!clientInfo) {
        throw new VdiError(VdiErrorType.APP_LAUNCH, `Missing client configuration for ${type} resource`);
    }
    dialog.entries = dialog.entries || [];
    // null check override: this function is only invoked when multiple clients available
    const caption = makeClientMsg(vdi.strings.select_client_message, clientInfo.native!.name);
    const selector = makeVdiSettings(type, caption, vdi);
    if (!selector) {
        throw new VdiError(VdiErrorType.APP_LAUNCH, `Invalid client configuration for ${type} resource`);
    }
    dialog.entries.unshift(selector);
}

function vdiInitSettingsDialog(dialog: IDialogConfiguration, vdi: Readonly<IVdiModel>) {
    dialog.entries = dialog.entries || [];
    if (vdi.hasCitrixResources) {
        // FIXME: should this come from customization?
        const citrixSelector = makeVdiSettings(REMOTE_DESKTOP_TYPE.CITRIX, '<strong>Citrix</strong>', vdi);
        if (citrixSelector) {
            dialog.entries.unshift(citrixSelector);
        }
    }
    if (vdi.hasVmwareViewResources) {
        const vmwareSelector = makeVdiSettings(REMOTE_DESKTOP_TYPE.VMWARE_VIEW, '<strong>VMware View</strong>', vdi);
        if (vmwareSelector) {
            dialog.entries.unshift(vmwareSelector);
        }
    }
}

app.subscribeDefault(EventTags.CONFIGURE, (_, page) => {
    if (page.pageType !== PageType.WEBTOP) {
        return;
    }
    const vdi = page.webtop.vdi;
    // Set default
    vdi.hasCitrixResources = false;
    vdi.hasVmwareViewResources = false;

    vdi.dialogs = vdiGetDialogTemplates(vdi.strings);
});

app.subscribe(EventTags.RESOURCE_STORE_CHANGED, ({ detail }, page) => {
    if (page.pageType !== PageType.WEBTOP) {
        return;
    }
    const vdi = page.webtop.vdi;
    /*
     * Check if Citrix or VMware resources are present and settings button should be displayed.
     */
    if ((!vdi.hasCitrixResources || !vdi.hasVmwareViewResources) && detail[ResourceType.REMOTE_DESKTOP]) {
        for (const resource of Object.values(detail[ResourceType.REMOTE_DESKTOP]!)) {
            if (!vdi.hasCitrixResources && resource.rdType === REMOTE_DESKTOP_TYPE.CITRIX) {
                vdi.hasCitrixResources = true;
            }
            if (!vdi.hasVmwareViewResources && resource.rdType === REMOTE_DESKTOP_TYPE.VMWARE_VIEW) {
                vdi.hasVmwareViewResources = true;
            }
            if (vdi.hasCitrixResources && vdi.hasVmwareViewResources) {
                break;
            }
        }
        page.general.header.showSettings = vdi.hasCitrixResources || vdi.hasVmwareViewResources;
    }
});

app.subscribe(EventTags.DIALOG_OPEN, (event, page) => {
    if (
        page.pageType !== PageType.WEBTOP ||
        typeof event.detail.dialog !== 'string' ||
        !(event.detail.dialog in page.webtop.vdi.dialogs)
    ) {
        return;
    }

    const vdi = page[page.pageType].vdi;
    const params = event.detail as IVdiDialogParams;
    const dialogId = event.detail.dialog;
    const resource = params.resource;

    /*
     * Do not show client selector if the package info is not loaded.
     * Try to load the info instead.
     */
    const needClientInfo =
        (dialogId === DialogType.VDI_LAUNCH_CLIENT && resource) || dialogId === DialogType.VDI_SETTINGS;

    if (needClientInfo && !vdi.clients) {
        // check if the fetch is complete
        if (params.clients) {
            vdi.clients = params.clients;
        } else {
            // FIXME: handle failure of async calls
            Promise.all([getCitrixClients(), getViewClients()])
                .catch((err) => {
                    console.error('Failed to load VDI client package info: ', err);
                    return [];
                })
                .then(([citrix, view]) => {
                    // retry with the same parameters
                    app.dispatch(EventTags.DIALOG_OPEN, {
                        ...event.detail,
                        clients: {
                            [REMOTE_DESKTOP_TYPE.CITRIX]: citrix,
                            [REMOTE_DESKTOP_TYPE.VMWARE_VIEW]: view,
                        },
                    });
                });
            // try again after loading client info
            event.preventDefault();
            event.stopPropagation();
            return;
        }
    }

    /*
     * Proceed to dialog initialization
     */
    const dialog = deepCopy<IDialogConfiguration>(vdi.dialogs[dialogId]);
    /* Remember resource for current launch sequence */
    dialog.resource = resource;
    try {
        switch (dialogId) {
            case DialogType.VDI_ERROR: {
                const error = VdiErrorType.fromError(params.error);
                const errorInfo = vdi.error[error];
                dialog.title = errorInfo.caption;
                dialog.body = errorInfo.message;
                if (params.error && 'detail' in params.error) {
                    // FIXME: better formatting for error detail.
                    dialog.body += `<div class="error-detail">${params.error.detail}</div>`;
                }
                break;
            }
            case DialogType.VDI_DESTINATION:
            case DialogType.VDI_LOGON:
                if (!resource) {
                    throw new VdiError(VdiErrorType.APP_LAUNCH, `Required parameter 'resource' is missing`);
                }
                break;
            case DialogType.VDI_LAUNCH_CLIENT:
                if (!resource) {
                    throw new VdiError(VdiErrorType.APP_LAUNCH, `Required parameter 'resource' is missing`);
                }
                vdiInitLaunchDialog(dialog, vdi, resource.rdType);
                break;
            case DialogType.VDI_SETTINGS:
                vdiInitSettingsDialog(dialog, vdi);
                break;
        }

        const citrixField = Dialog.getField(dialog, REMOTE_DESKTOP_TYPE.CITRIX.toString());
        // set Citrix field value if it is selectable
        if (citrixField && !citrixField.disabled && citrixField.type !== INPUT.HIDDEN) {
            citrixField.value = getUserPreferredClient(REMOTE_DESKTOP_TYPE.CITRIX);
        }

        const viewField = Dialog.getField(dialog, REMOTE_DESKTOP_TYPE.VMWARE_VIEW.toString());
        // set View field value if it is selectable
        if (viewField && !viewField.disabled && viewField.type !== INPUT.HIDDEN) {
            viewField.value = getUserPreferredClient(REMOTE_DESKTOP_TYPE.VMWARE_VIEW);
        }
    } catch (err) {
        /* Propagate error to dialog caller */
        if (event.detail.callback) {
            event.detail.callback.reject(err);
        }
        return;
    }

    event.detail.dialog = dialog;
});

function savePreferredClient(dialog: IDialogConfiguration) {
    const citrixField = Dialog.getField(dialog, REMOTE_DESKTOP_TYPE.CITRIX);
    const vmwareField = Dialog.getField(dialog, REMOTE_DESKTOP_TYPE.VMWARE_VIEW);

    if (citrixField && !citrixField.disabled && citrixField.type !== INPUT.HIDDEN && citrixField.value) {
        setUserPreferredClient(REMOTE_DESKTOP_TYPE.CITRIX, citrixField.value as string);
    }

    if (vmwareField && !vmwareField.disabled && vmwareField.type !== INPUT.HIDDEN && vmwareField.value) {
        setUserPreferredClient(REMOTE_DESKTOP_TYPE.VMWARE_VIEW, vmwareField.value as string);
    }
}

function getFieldValues(dialog?: IDialogConfiguration) {
    const result = {};
    if (dialog && dialog.entries) {
        for (const entry of dialog.entries) {
            if ('name' in entry && !entry.disabled && entry.type !== INPUT.HIDDEN) {
                result[entry.name] = entry.value;
            }
        }
    }
    return result;
}

app.subscribe(EventTags.DIALOG_ACTION, ({ detail }, page) => {
    if (page.pageType !== PageType.WEBTOP) {
        return;
    }
    const vdi = page.webtop.vdi;
    if (detail.dialog && detail.dialog.id in vdi.dialogs && detail.action === CommonDialogAction.OK) {
        const dialog = Dialog.get(page, detail.dialog) || undefined;
        switch (detail.dialog.id) {
            case DialogType.VDI_SETTINGS:
            case DialogType.VDI_LAUNCH_CLIENT:
                if (dialog) {
                    savePreferredClient(dialog);
                }
            /* fallthrough */
            default:
                Dialog.resolve(dialog, getFieldValues(dialog));
                break;
        }
    }
});

/** TODO: This should be standardised  */
function makeClientMsg(pattern: string, clientName: string, clientUrl?: string): string {
    let result = pattern.replace('[VDI_CLIENT_NAME]', clientName);
    if (clientUrl) {
        result = result.replace('[VDI_CLIENT_URL]', clientUrl);
    }
    return result;
}

function makeVdiSettings(rdType: REMOTE_DESKTOP_TYPE, caption: string, vdi: Readonly<IVdiModel>) {
    // null check override: clients is checked and set before the call
    const clientInfo = vdi.clients![rdType];

    if (!clientInfo || (!clientInfo.native && !clientInfo.html5)) {
        // abnormal situation: there is no valid configuration option available
        console.error(`Invalid client configuration for ${rdType} resources`);
        return undefined;
    }

    const clientSelector: IDialogEntry = {
        name: rdType,
        type: INPUT.RADIO,
        caption,
        richText: true,
        options: [],
        // 'value' is NOT set here
        value: undefined,
    };

    // both vdi.view_admin_preferred_client and citrix_admin_preferred_client are session variables
    // so we can't predict values here
    let adminPreferredClient: VDI_CLIENT | null = null;
    if (rdType === REMOTE_DESKTOP_TYPE.VMWARE_VIEW) {
        adminPreferredClient = vdi.strings.view_admin_preferred_client as VDI_CLIENT;
    } else if (rdType === REMOTE_DESKTOP_TYPE.CITRIX) {
        adminPreferredClient = vdi.strings.citrix_admin_preferred_client as VDI_CLIENT;
    }

    // Select preferred client and disable input if available
    // Input will be still displayed because it contains download link.
    if (adminPreferredClient && clientInfo[adminPreferredClient]) {
        clientSelector.caption +=
            '<br />' +
            makeClientMsg(vdi.strings.settings_admin_enforced_client_message, clientInfo[adminPreferredClient]!.name);
        clientSelector.disabled = true;
        clientSelector.value = adminPreferredClient;
    }

    const { html5, native } = clientInfo;
    if (native) {
        clientSelector.options.push({
            value: VDI_CLIENT.NATIVE,
            text:
                `<strong>${native.name}</strong><br />` +
                makeClientMsg(
                    vdi.strings.native_client_download_message,
                    native.name || caption,
                    native.downloadUrl || CITRIX_DEFAULT_URL
                ),
        });
    }
    if (html5) {
        clientSelector.options.push({
            value: VDI_CLIENT.HTML5,
            text: `<strong>${html5.name}</strong>`,
        });
    }

    return clientSelector;
}
